import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

interface UseNutritionRealtimeOptions {
  enabled?: boolean;
  invalidateQueries?: string[];
}

export function useNutritionRealtime(options: UseNutritionRealtimeOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { enabled = true, invalidateQueries = [] } = options;

  useEffect(() => {
    if (!enabled || !user) return;

    // Create channels for different nutrition data types
    const channels = [
      {
        name: 'nutrition-summary-changes',
        table: 'nutrition_daily_summary',
        queries: ['nutrition-summary', 'daily-summary', 'net-calorie-analysis']
      },
      {
        name: 'meals-changes', 
        table: 'meals',
        queries: ['nutrition-summary', 'meals-today', 'daily-summary', 'net-calorie-analysis']
      },
      {
        name: 'food-entries-changes',
        table: 'food_entries', 
        queries: ['nutrition-summary', 'meals-today', 'daily-summary']
      },
      {
        name: 'nutrition-goals-changes',
        table: 'nutrition_goals',
        queries: ['nutrition-goals', 'nutrition-summary', 'daily-summary']
      },
      {
        name: 'activities-changes',
        table: 'running_performance',
        queries: ['net-calorie-analysis', 'workout-nutrition-sync', 'performance-correlations']
      },
      {
        name: 'fitness-changes',
        table: 'general_fitness',
        queries: ['net-calorie-analysis', 'workout-nutrition-sync', 'performance-correlations']
      },
      {
        name: 'hydration-changes',
        table: 'hydration_logs',
        queries: ['hydration-for-date']
      }
    ];

    const subscriptions = channels.map(({ name, table, queries }) => {
      const channel = supabase
        .channel(name)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table,
            filter: `user_id=eq.${user.id}`
          },
          () => {
            // Invalidate relevant queries
            [...queries, ...invalidateQueries].forEach(queryKey => {
              queryClient.invalidateQueries({ 
                queryKey: [queryKey, user.id],
                exact: false 
              });
            });
          }
        )
        .subscribe();

      return channel;
    });

    return () => {
      subscriptions.forEach(channel => {
        supabase.removeChannel(channel);
      });
    };
  }, [user, enabled, queryClient, invalidateQueries]);
}